import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router';
import {
  BackButton,
  Box,
  Center,
  CustomSpinner,
  FloatingContainer,
  Row,
  Screen,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Text,
} from '~/commonComponents';
import { JobPostingStatus } from '~/domain/PermanentJobs';
import { useJobProfessionDisplay } from '~/hooks';
import { ProfessionTypeEnum } from '~/models';
import { Colors } from '~/themes/colors';
import {
  CandidateTabView,
  CloseJobFeedbackPopup,
  CloseJobSuccessPopup,
  ConfirmCloseJobPopup,
  JobDetailsTabView,
  JobOverviewHeader,
  OnsiteInterviewTabView,
  SelectCandidatesPopup,
  useCloseJobFlow,
} from './components';
import { TJobOverviewPageRoutes } from './Types';
import { useJobOverview } from './useJobOverview';
import { useJobOverviewData } from './useJobOverviewData';
import { useSafeLocationState } from './useSafeLocationState';

export const JobOverview = () => {
  const queryClient = useQueryClient();
  const { postingId } = useParams<TJobOverviewPageRoutes>();

  const { activeTab, onChangeTab } = useJobOverview();

  const {
    jobPosting,
    hasCandidatesToHire,
    hasCandidateScheduledOnsiteInterviews,
    hasScheduledOnsiteInterviews,
    isLoading,
  } = useJobOverviewData(postingId);

  const {
    currentStep: closeJobStep,
    selectedCandidateIds,
    hasHires,
    isClosingJob,
    startFlow: startCloseJobFlow,
    handleSelectCandidates,
    handleGoBack,
    handleConfirmClose,
    handleFeedbackSkip,
    handleFeedbackSubmit,
    closeFlow: endCloseJobFlow,
  } = useCloseJobFlow({ postingId, hasCandidatesToHire });
  const { fromOnsiteInterviewCreation } = useSafeLocationState();

  const { title, professionType, subProfession } = jobPosting || {};

  const professionDisplay = useJobProfessionDisplay({
    profession: professionType || ProfessionTypeEnum.RDH,
    subProfession,
  });

  const handleCloseJobSuccess = () => {
    endCloseJobFlow();
    // Invalidate query to refetch latest data in parent component
    queryClient.invalidateQueries({
      queryKey: ['GET_JOB_POSTING_DETAIL', postingId],
    });
  };

  if (isLoading) {
    return (
      <Center flex={1}>
        <CustomSpinner isLoading />
      </Center>
    );
  }

  return (
    <Screen>
      <FloatingContainer borderRadius={0} paddingBlock={48} paddingInline={16} height="100%">
        <Row position="relative" alignItems="center" justifyContent="center">
          <Box position="absolute" left={0}>
            <BackButton
              url={fromOnsiteInterviewCreation ? '/permanent-jobs/postings' : undefined}
            />
          </Box>
          <Text bold fontSize={32} textAlign="center">
            Job Overview
          </Text>
        </Row>
        <JobOverviewHeader
          closedAt={jobPosting?.closedAt ? new Date(jobPosting?.closedAt) : undefined}
          hasScheduledOnsiteInterviews={hasScheduledOnsiteInterviews}
          id={postingId || ''}
          officeName={jobPosting?.officeName || ''}
          postedAt={jobPosting?.publishedAt ? new Date(jobPosting?.publishedAt) : undefined}
          professional={professionDisplay || ''}
          status={jobPosting?.status as JobPostingStatus}
          suspendedAt={jobPosting?.suspendedAt ? new Date(jobPosting?.suspendedAt) : undefined}
          officePlanId={jobPosting?.officePlanId || undefined}
          title={title || ''}
          onCloseJob={startCloseJobFlow}
        />
        <Tabs
          variant="background"
          initialTab={activeTab}
          onChangeTab={onChangeTab}
          tabListStyle={{
            backgroundColor: Colors.neutral_50,
            borderRadius: 9,
            paddingBlock: 3,
            paddingInline: 8,
          }}
        >
          <TabList>
            <Tab dataCy="job-details-tab" title="Job Details" />
            <Tab dataCy="candidates-tab" title="Candidates" />
            <Tab dataCy="onsite-interviews-tab" title="Onsite Interviews" />
          </TabList>
          <TabPanel>
            <JobDetailsTabView jobPosting={jobPosting} />
            <CandidateTabView jobPostingId={postingId || ''} />
            <OnsiteInterviewTabView jobPostingId={postingId || ''} />
          </TabPanel>
        </Tabs>
      </FloatingContainer>

      {closeJobStep === 'select-candidates' && (
        <SelectCandidatesPopup
          jobPostingId={postingId || ''}
          onClose={endCloseJobFlow}
          onConfirm={handleSelectCandidates}
          defaultSelectedCandidateIds={selectedCandidateIds}
        />
      )}

      {closeJobStep === 'confirm-close' && (
        <ConfirmCloseJobPopup
          onGoBack={handleGoBack}
          onConfirm={handleConfirmClose}
          hasScheduledOnsiteInterviews={hasCandidateScheduledOnsiteInterviews}
          isLoading={isClosingJob}
        />
      )}

      {closeJobStep === 'feedback' && (
        <CloseJobFeedbackPopup onClose={handleFeedbackSkip} onSubmit={handleFeedbackSubmit} />
      )}

      {closeJobStep === 'success' && (
        <CloseJobSuccessPopup onClose={handleCloseJobSuccess} hasHires={hasHires} />
      )}
    </Screen>
  );
};
